/**
 * IXVerify v2 — second-check request sheet (ixv2 WP-3.2; live WP-4.1;
 * plan §3.3).
 *
 * Site-user picker → ping a verifier. The backing routes (`/site-users`,
 * `/sessions/:id/second-check`) are LIVE since WP-4.1; the sheet still
 * degrades cleanly when the rollback flag has the feature off (503
 * IXV_SECOND_CHECK_DISABLED) or in a build running against an older backend
 * (501 — the WP-2.3 convention).
 */
import { useEffect, useState } from 'react';
import { Loader2, UserCheck, X } from 'lucide-react';
import { errorMessage, is501, isSecondCheckDisabled, listSiteUsers, requestSecondCheck, type SiteUserLite } from '../../lib/api';

export default function SecondCheckSheet({
  sessionId,
  onClose,
  onRequested,
}: {
  sessionId: string;
  onClose: () => void;
  onRequested: () => void;
}) {
  const [users, setUsers] = useState<SiteUserLite[] | null>(null);
  const [stubbed, setStubbed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSiteUsers()
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((err) => {
        if (cancelled) return;
        if (is501(err)) setStubbed(true);
        else setError(errorMessage(err, 'Could not load site users'));
        setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function request(verifierId: string) {
    setSending(verifierId);
    setError(null);
    try {
      await requestSecondCheck(sessionId, verifierId);
      onRequested();
    } catch (err) {
      if (is501(err) || isSecondCheckDisabled(err)) setStubbed(true);
      else setError(errorMessage(err, 'Could not request the second check'));
    } finally {
      setSending(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Request second check"
    >
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800" data-testid="second-check-sheet">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <UserCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" /> Second check
          </h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {stubbed ? (
          <p className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
            Second-person verification is not available on this deployment right now. Your report is already published.
          </p>
        ) : users == null ? (
          <div className="mt-4 flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 motion-reduce:animate-none dark:text-primary-400" aria-hidden="true" />
          </div>
        ) : users.length === 0 && !error ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No other users at this site yet.</p>
        ) : (
          <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto">
            {users.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  disabled={sending != null}
                  onClick={() => void request(u.id)}
                  className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 text-left text-sm text-gray-900 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 motion-reduce:transition-none dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <span className="truncate">
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id}
                  </span>
                  {sending === u.id && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
